/* ===========================================================================
   ne-chat-widget.js — site half of the Telegram support bridge.

   Talks to one Apps Script web app (the `data-endpoint` on #ne-chat) with:
     POST ?action=start    {name,email,text,page,hp}   -> {conv_id, messages}
     POST ?action=message  {conv_id,text}              -> {ok, msg_id}
     GET  ?action=poll&conv_id=…&after=…               -> {messages:[…]}

   Requests are sent as text/plain so the browser treats them as simple
   requests: Apps Script cannot answer a CORS preflight, so any JSON
   content-type would fail before it left the page.

   No credentials live here. The bot token and group id stay in the Apps
   Script project's Script Properties.
   =========================================================================== */
(function () {
  'use strict';

  var root = document.getElementById('ne-chat');
  if (!root) return;

  var ENDPOINT = (root.getAttribute('data-endpoint') || '').trim();
  // No endpoint, no widget. The only thing this file ever talks to is the
  // backend configured in theme settings — there is no other code path.
  if (!ENDPOINT) { root.remove(); return; }

  var POLL_MS  = parseInt(root.getAttribute('data-poll-ms'), 10) || 4500;
  var KEY      = 'ne_chat_conv';
  var NUDGE_KEY = 'ne_chat_nudge_off';
  var NUDGE_AFTER_MS = 6000;
  var MAX_LEN  = 2000;
  // 5MB, not 10: the payload is base64 on the way to Apps Script, which adds a
  // third again, and Apps Script starts refusing bodies well before 10MB does.
  var MAX_FILE = 5 * 1024 * 1024;
  var OK_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
  var SEND_GAP = 1000;                                  // 1 message/sec, client side

  var bubble    = document.getElementById('ne-chat-bubble');
  var panel     = document.getElementById('ne-chat-panel');
  var closeBtn  = document.getElementById('ne-chat-close');
  var intro     = document.getElementById('ne-chat-intro');
  var introErr  = document.getElementById('ne-chat-intro-error');
  var nameEl    = document.getElementById('ne-chat-name');
  var emailEl   = document.getElementById('ne-chat-email');
  var firstEl   = document.getElementById('ne-chat-first');
  var hpEl      = document.getElementById('ne-chat-company');
  var startBtn  = document.getElementById('ne-chat-start');
  var log       = document.getElementById('ne-chat-log');
  var composer  = document.getElementById('ne-chat-composer');
  var input     = document.getElementById('ne-chat-input');
  var dot       = document.getElementById('ne-chat-dot');
  var attachBtn = document.getElementById('ne-chat-attach');
  var fileInput = document.getElementById('ne-chat-file');
  var attachBox = document.getElementById('ne-chat-attach-preview');
  var nudge     = document.getElementById('ne-chat-nudge');
  var nudgeX    = document.getElementById('ne-chat-nudge-x');

  var convId = null;
  var lastId = 0;
  var timer = null;
  var lastSentAt = 0;
  var open = false;

  try { convId = localStorage.getItem(KEY) || null; } catch (e) { convId = null; }

  /* ---- transport -------------------------------------------------------- */

  function post(action, body) {
    return fetch(ENDPOINT + '?action=' + action, {
      method: 'POST',
      // text/plain keeps this a "simple request" — no preflight for Apps Script
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json(); });
  }

  function get(action, query) {
    var qs = Object.keys(query).map(function (k) {
      return encodeURIComponent(k) + '=' + encodeURIComponent(query[k]);
    }).join('&');
    return fetch(ENDPOINT + '?action=' + action + '&' + qs).then(function (r) { return r.json(); });
  }

  /* ---- incoming-message signals ----------------------------------------
     Sound, launcher pulse and a blinking tab title. All three answer the same
     question — "something arrived while you were not looking" — so they are
     armed and cleared in one place.

     The tone is synthesised rather than loaded: a two-note rise, E5 -> A5,
     ~100ms each with a short decay. No audio file to fetch, nothing to cache,
     and no request that could fail on a slow connection.                     */

  var audioCtx = null;
  var audioReady = false;

  // Browsers refuse to start an AudioContext before the visitor has interacted
  // with the page, so it is created on the first gesture and then kept.
  function armAudio() {
    if (audioReady) return;
    try {
      var Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) { audioReady = true; return; }     // no Web Audio: stay silent
      audioCtx = new Ctx();
      if (audioCtx.state === 'suspended') audioCtx.resume();
      audioReady = true;
    } catch (e) { audioReady = true; }             // never let this break the chat
  }
  ['pointerdown', 'keydown', 'touchstart'].forEach(function (evt) {
    document.addEventListener(evt, armAudio, { once: true, passive: true });
  });

  function note(freq, startAt, ms, peak) {
    var osc = audioCtx.createOscillator();
    var gain = audioCtx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, startAt);
    // a ramped envelope instead of a hard start/stop: a square edge on a sine
    // is what makes notification tones sound like a click
    gain.gain.setValueAtTime(0.0001, startAt);
    gain.gain.exponentialRampToValueAtTime(peak, startAt + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, startAt + ms / 1000);
    osc.connect(gain);
    gain.connect(audioCtx.destination);
    osc.start(startAt);
    osc.stop(startAt + ms / 1000 + 0.02);
  }

  function chime() {
    if (!audioCtx || audioCtx.state === 'closed') return;
    try {
      if (audioCtx.state === 'suspended') audioCtx.resume();
      var t = audioCtx.currentTime;
      note(660, t, 105, 0.17);            // E5
      note(880, t + 0.105, 120, 0.17);    // A5 — total 225ms
    } catch (e) { /* audio is a nicety, never a failure path */ }
  }

  var baseTitle = document.title;
  var titleTimer = null;

  function blinkTitle(on) {
    if (on) {
      if (titleTimer) return;
      var flip = false;
      titleTimer = setInterval(function () {
        flip = !flip;
        document.title = flip ? 'New message · New Elements' : baseTitle;
      }, 1200);
    } else if (titleTimer) {
      clearInterval(titleTimer);
      titleTimer = null;
      document.title = baseTitle;
    }
  }

  function clearUnread() {
    dot.hidden = true;
    bubble.classList.remove('has-news');
    blinkTitle(false);
  }

  // Fires for an agent message the visitor cannot currently see: panel closed,
  // or the tab is in the background. An open panel in a focused tab stays quiet.
  function signalIncoming() {
    var unseen = !open || document.hidden;
    if (!unseen) return;
    dot.hidden = false;
    bubble.classList.add('has-news');
    chime();
    if (document.hidden) blinkTitle(true);
  }

  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) {
      baseTitle = baseTitle || document.title;
      if (open) clearUnread(); else blinkTitle(false);
    }
  });

  /* ---- rendering -------------------------------------------------------- */

  function time(ts) {
    var d = ts ? new Date(ts) : new Date();
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  function bubbleEl(msg, state) {
    var wrap = document.createElement('div');
    wrap.className = 'ne-chat__msg ne-chat__msg--' + (msg.from === 'agent' ? 'agent' : 'client');
    if (state) wrap.classList.add('is-' + state);
    if (msg.id) wrap.setAttribute('data-id', msg.id);

    if (msg.file) wrap.appendChild(fileEl(msg));

    if (msg.text) {
      var body = document.createElement('p');
      body.className = 'ne-chat__bubble-text';
      body.textContent = msg.text;               // textContent: never inject markup
      wrap.appendChild(body);
    }

    var meta = document.createElement('span');
    meta.className = 'ne-chat__meta';
    meta.textContent = state === 'sending' ? 'Sending…' : time(msg.at);
    wrap.appendChild(meta);
    return wrap;
  }

  function kb(n) {
    n = Number(n) || 0;
    return n >= 1024 * 1024 ? (n / 1048576).toFixed(1) + ' MB' : Math.max(1, Math.round(n / 1024)) + ' KB';
  }

  /* Renders one attachment. Bytes are never in the message itself: the log
     shows a placeholder and asks the backend for the file separately, so a
     long transcript does not drag megabytes of base64 through every poll.

     Agent files come from Telegram, whose own file URLs embed the bot token.
     They are therefore fetched through the backend, which downloads them
     server-side and hands back bytes — the token never reaches this page. */
  function fileEl(msg) {
    var f = msg.file || {};
    var box = document.createElement('div');
    box.className = 'ne-chat__file';

    var isImage = /^image\//.test(f.type || '');
    var note = document.createElement('span');
    note.className = 'ne-chat__file-loading';
    note.textContent = isImage ? 'Loading image…' : (f.name || 'Attachment');
    box.appendChild(note);

    if (!msg.id || !convId) return box;

    get('file', { conv_id: convId, msg_id: msg.id })
      .then(function (res) {
        if (!res || !res.ok || !res.b64) throw new Error('no file');
        var bin = atob(res.b64);
        var bytes = new Uint8Array(bin.length);
        for (var i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
        var blob = new Blob([bytes], { type: res.type || f.type || 'application/octet-stream' });
        var url = URL.createObjectURL(blob);
        box.innerHTML = '';

        if (/^image\//.test(res.type || f.type || '')) {
          var img = document.createElement('img');
          img.src = url;
          img.alt = res.name || f.name || 'Attachment';
          img.addEventListener('click', function () { window.open(url, '_blank', 'noopener'); });
          box.appendChild(img);
        } else {
          var card = document.createElement('a');
          card.className = 'ne-chat__file-card';
          card.href = url;
          card.download = res.name || f.name || 'attachment.pdf';
          card.rel = 'noopener';
          var icon = document.createElement('i');
          icon.textContent = 'PDF';
          var label = document.createElement('b');
          label.textContent = res.name || f.name || 'Document';
          card.appendChild(icon);
          card.appendChild(label);
          box.appendChild(card);
        }
        log.scrollTop = log.scrollHeight;
      })
      .catch(function () {
        note.textContent = (f.name || 'Attachment') + ' — could not load';
      });

    return box;
  }

  /* ---- picking and sending a file --------------------------------------- */

  var picked = null;

  function clearPick() {
    picked = null;
    if (fileInput) fileInput.value = '';
    if (attachBox) { attachBox.hidden = true; attachBox.innerHTML = ''; }
  }

  function showPick(file) {
    attachBox.innerHTML = '';
    attachBox.hidden = false;

    if (/^image\//.test(file.type)) {
      var img = document.createElement('img');
      img.className = 'ne-chat__attach-thumb';
      img.alt = '';
      img.src = URL.createObjectURL(file);
      attachBox.appendChild(img);
    } else {
      var doc = document.createElement('span');
      doc.className = 'ne-chat__attach-doc';
      doc.textContent = 'PDF';
      attachBox.appendChild(doc);
    }

    var name = document.createElement('span');
    name.className = 'ne-chat__attach-name';
    name.textContent = file.name;
    var size = document.createElement('span');
    size.className = 'ne-chat__attach-size';
    size.textContent = kb(file.size);
    name.appendChild(size);
    attachBox.appendChild(name);

    var drop = document.createElement('button');
    drop.type = 'button';
    drop.className = 'ne-chat__attach-drop';
    drop.setAttribute('aria-label', 'Remove attachment');
    drop.textContent = '\u00d7';
    drop.addEventListener('click', clearPick);
    attachBox.appendChild(drop);
  }

  function pickFile(file) {
    if (!file) return;
    if (OK_TYPES.indexOf(file.type) === -1) {
      append({ from: 'agent', text: 'Only JPEG, PNG, WebP or PDF files can be sent here.', at: Date.now() });
      clearPick();
      return;
    }
    if (file.size > MAX_FILE) {
      append({ from: 'agent', text: 'That file is ' + kb(file.size) + '. The limit is 5 MB — please send a smaller one, or email it to us.', at: Date.now() });
      clearPick();
      return;
    }
    picked = file;
    showPick(file);
  }

  function markFailed(el, text) {
    el.classList.remove('is-sending');
    el.classList.add('is-failed');
    el.querySelector('.ne-chat__meta').textContent = text;
  }

  function sendFile(file, caption) {
    if (!convId) return;
    var reader = new FileReader();
    var el = append({ from: 'client', text: caption || '', at: Date.now(), file: { name: file.name, type: file.type } }, 'sending');
    // the placeholder cannot fetch itself yet — it has no id — so show the
    // local copy straight away
    var holder = el.querySelector('.ne-chat__file');
    if (holder) {
      holder.innerHTML = '';
      if (/^image\//.test(file.type)) {
        var img = document.createElement('img');
        img.src = URL.createObjectURL(file);
        img.alt = file.name;
        holder.appendChild(img);
      } else {
        var card = document.createElement('span');
        card.className = 'ne-chat__file-card';
        var i2 = document.createElement('i'); i2.textContent = 'PDF';
        var b2 = document.createElement('b'); b2.textContent = file.name;
        card.appendChild(i2); card.appendChild(b2);
        holder.appendChild(card);
      }
    }
    log.scrollTop = log.scrollHeight;

    reader.onerror = function () { failed(el, caption || ''); };
    reader.onload = function () {
      var b64 = String(reader.result).split(',')[1] || '';
      post('upload', {
        conv_id: convId,
        name: file.name,
        type: file.type,
        size: file.size,
        text: caption || '',
        data: b64
      })
        .then(function (res) {
          if (!res || !res.ok) throw new Error(res && res.error ? res.error : 'rejected');
          el.classList.remove('is-sending');
          if (res.msg_id) { el.setAttribute('data-id', res.msg_id); lastId = Math.max(lastId, Number(res.msg_id) || 0); }
          el.querySelector('.ne-chat__meta').textContent = time(Date.now());
        })
        .catch(function (err) {
          var reason = String((err && err.message) || '');
          // Errors the backend states plainly are final; say so and stop.
          if (/rate limit/i.test(reason)) return markFailed(el, 'Not sent · too many files, try again in a few minutes');
          if (/too large/i.test(reason)) return markFailed(el, 'Not sent · file too large');
          if (/unsupported/i.test(reason)) return markFailed(el, 'Not sent · unsupported file type');

          // Anything else is ambiguous. Apps Script answers a POST with a 302,
          // and the follow-up request occasionally lands on an instance that
          // re-runs it as a GET and reports "unknown action" — after the file
          // has already gone through. Declaring failure there makes the visitor
          // send it twice. So ask the transcript who is right.
          setTimeout(function () {
            get('poll', { conv_id: convId, after: 0 })
              .then(function (res) {
                var arrived = (res && res.messages || []).some(function (m) {
                  return m.file && m.file.name === file.name && m.from === 'client';
                });
                if (arrived) {
                  el.classList.remove('is-sending');
                  el.querySelector('.ne-chat__meta').textContent = time(Date.now());
                } else {
                  markFailed(el, 'Not sent');
                }
              })
              .catch(function () { markFailed(el, 'Not sent'); });
          }, 2500);
        });
    };
    reader.readAsDataURL(file);
  }

  function append(msg, state) {
    var el = bubbleEl(msg, state);
    log.appendChild(el);
    log.scrollTop = log.scrollHeight;
    return el;
  }

  function failed(el, text, retry) {
    el.classList.remove('is-sending');
    el.classList.add('is-failed');
    var meta = el.querySelector('.ne-chat__meta');
    meta.textContent = 'Not sent · ';
    var again = document.createElement('button');
    again.type = 'button';
    again.className = 'ne-chat__retry';
    again.textContent = 'Retry';
    again.addEventListener('click', function () { el.remove(); send(text); });
    meta.appendChild(again);
  }

  function showChat() {
    intro.hidden = true;
    log.hidden = false;
    composer.hidden = false;
  }

  /* ---- conversation ----------------------------------------------------- */

  function startConversation(e) {
    e.preventDefault();
    introErr.hidden = true;
    if (hpEl && hpEl.value) return;               // honeypot: silently drop bots

    var name = nameEl.value.trim();
    var email = emailEl.value.trim();
    var text = firstEl.value.trim().slice(0, MAX_LEN);
    if (!name || !/.+@.+\..+/.test(email) || !text) {
      introErr.textContent = 'Please fill in your name, a valid email and a message.';
      introErr.hidden = false;
      return;
    }

    startBtn.disabled = true;
    startBtn.textContent = 'Starting…';
    post('start', { name: name, email: email, text: text, page: location.href, hp: '' })
      .then(function (res) {
        if (!res || !res.ok || !res.conv_id) throw new Error('bad response');
        convId = String(res.conv_id);
        try { localStorage.setItem(KEY, convId); } catch (e) {}
        showChat();
        (res.messages || [{ from: 'client', text: text, at: Date.now(), id: 1 }]).forEach(function (m) {
          append(m); lastId = Math.max(lastId, Number(m.id) || 0);
        });
        startPolling();
        input.focus();
      })
      .catch(function () {
        introErr.textContent = 'Could not reach support. Please try again, or email ' +
          (root.getAttribute('data-shop-email') || 'hello@newelements.org') + '.';
        introErr.hidden = false;
      })
      .then(function () {
        startBtn.disabled = false;
        startBtn.textContent = 'Start chat';
      });
  }

  function send(text) {
    text = String(text || '').trim().slice(0, MAX_LEN);
    if (!text || !convId) return;
    var now = Date.now();
    if (now - lastSentAt < SEND_GAP) return;      // 1 per second
    lastSentAt = now;

    var el = append({ from: 'client', text: text, at: now }, 'sending');
    post('message', { conv_id: convId, text: text })
      .then(function (res) {
        if (!res || !res.ok) throw new Error('rejected');
        el.classList.remove('is-sending');
        if (res.msg_id) { el.setAttribute('data-id', res.msg_id); lastId = Math.max(lastId, Number(res.msg_id) || 0); }
        el.querySelector('.ne-chat__meta').textContent = time(now);
      })
      .catch(function () { failed(el, text); });
  }

  /* ---- polling ---------------------------------------------------------- */

  function poll() {
    if (!convId) return;
    get('poll', { conv_id: convId, after: lastId })
      .then(function (res) {
        if (!res || !res.messages) return;
        res.messages.forEach(function (m) {
          var id = Number(m.id) || 0;
          if (id && id <= lastId) return;
          if (log.querySelector('[data-id="' + id + '"]')) return;
          lastId = Math.max(lastId, id);
          if (m.from === 'client') return;        // our own, already on screen
          append(m);
          signalIncoming();
        });
      })
      .catch(function () { /* transient network trouble: the next tick retries */ });
  }

  function startPolling() {
    if (timer) clearInterval(timer);
    timer = setInterval(poll, POLL_MS);
    poll();
  }

  /* ---- history for a returning visitor ---------------------------------- */

  if (convId) {
    showChat();
    get('poll', { conv_id: convId, after: 0 })
      .then(function (res) {
        if (!res || !res.ok) throw new Error('gone');
        (res.messages || []).forEach(function (m) {
          append(m); lastId = Math.max(lastId, Number(m.id) || 0);
        });
        startPolling();
      })
      .catch(function () {
        // conversation no longer known to the backend — start fresh
        try { localStorage.removeItem(KEY); } catch (e) {}
        convId = null;
        intro.hidden = false; log.hidden = true; composer.hidden = true;
      });
  }

  /* ---- proactive nudge -------------------------------------------------- */

  var nudgeTimer = null;

  function nudgeDismissed() {
    try { return localStorage.getItem(NUDGE_KEY) === '1'; } catch (e) { return false; }
  }

  function hideNudge(remember) {
    if (nudgeTimer) { clearTimeout(nudgeTimer); nudgeTimer = null; }
    if (!nudge) return;
    nudge.classList.remove('is-on');
    // wait out the fade before pulling it from the layout
    setTimeout(function () { if (nudge && !nudge.classList.contains('is-on')) nudge.hidden = true; }, 240);
    if (remember) { try { localStorage.setItem(NUDGE_KEY, '1'); } catch (e) {} }
  }

  function showNudge() {
    // A backgrounded tab does not run requestAnimationFrame, and the first
    // version primed the transition inside one — so the card unhid at opacity 0
    // and stayed invisible for the rest of the visit. Forcing a reflow gives
    // the transition its start state without depending on a callback firing.
    nudge.hidden = false;
    void nudge.offsetWidth;
    nudge.classList.add('is-on');
  }

  function scheduleNudge() {
    if (!nudge || nudgeDismissed()) return;
    nudgeTimer = setTimeout(function () {
      // conditions are re-checked at fire time, not at schedule time: six
      // seconds is long enough for the visitor to have opened the chat already
      if (open || nudgeDismissed()) return;
      // Nobody is looking at a background tab; wait for them to come back
      // rather than spending the one nudge on an empty room.
      if (document.hidden) {
        document.addEventListener('visibilitychange', function again() {
          if (document.hidden) return;
          document.removeEventListener('visibilitychange', again);
          if (!open && !nudgeDismissed() &&
              !(window.matchMedia && window.matchMedia('(max-width: 560px)').matches)) showNudge();
        });
        return;
      }
      if (window.matchMedia && window.matchMedia('(max-width: 560px)').matches) return;
      showNudge();
    }, NUDGE_AFTER_MS);
  }

  if (nudge) {
    nudge.addEventListener('click', function (e) {
      if (e.target === nudgeX) return;               // the × is handled below
      hideNudge(true);
      setOpen(true);
    });
  }
  if (nudgeX) {
    var dismiss = function (e) {
      e.stopPropagation();
      e.preventDefault();
      hideNudge(true);
    };
    nudgeX.addEventListener('click', dismiss);
    nudgeX.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') dismiss(e);
    });
  }

  /* ---- open / close ----------------------------------------------------- */

  function setOpen(next) {
    open = next;
    panel.hidden = !next;
    bubble.setAttribute('aria-expanded', String(next));
    bubble.classList.toggle('is-open', next);
    if (next) {
      armAudio();                                  // opening counts as a gesture
      hideNudge(true);                             // asked and answered
      clearUnread();
      (convId ? input : nameEl).focus({ preventScroll: true });
      log.scrollTop = log.scrollHeight;
    }
  }

  bubble.addEventListener('click', function () { setOpen(!open); });
  closeBtn.addEventListener('click', function () { setOpen(false); bubble.focus(); });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && open) { setOpen(false); bubble.focus(); }
  });

  intro.addEventListener('submit', startConversation);
  if (attachBtn && fileInput) {
    attachBtn.addEventListener('click', function () { armAudio(); fileInput.click(); });
    fileInput.addEventListener('change', function () { pickFile(fileInput.files && fileInput.files[0]); });
  }

  composer.addEventListener('submit', function (e) {
    e.preventDefault();
    var v = input.value;
    input.value = '';
    input.style.height = 'auto';
    if (picked) {
      var f = picked;
      clearPick();
      sendFile(f, v.trim().slice(0, MAX_LEN));    // the text rides along as the caption
      return;
    }
    send(v);
  });
  input.addEventListener('keydown', function (e) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); composer.requestSubmit(); }
  });
  input.addEventListener('input', function () {
    input.style.height = 'auto';
    input.style.height = Math.min(96, input.scrollHeight) + 'px';
  });

  scheduleNudge();
})();

/* --------------------------------------------------------------------------
   Bottom-bar clearance.

   Two different fixed bars can own the bottom edge on a phone: the theme's
   mobile nav (.m-mobile-sticky-bar, every template but the product page) and
   the PDP's own buy bar (#ne-sticky). Whichever is on screen, the launcher
   used to sit on top of it and cover a nav cell.

   Rather than hard-code either height, publish the tallest visible one as
   --ne-bottom-bar-h and let the stylesheet lift the launcher and the panel by
   it. Nothing visible means 0, which is the desktop case.
   -------------------------------------------------------------------------- */
(function () {
  var SELECTORS = '.m-mobile-sticky-bar, #ne-sticky, #ne-mh-nav';

  function onScreen(el) {
    if (!el) return false;
    var cs = getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden') return false;
    return el.getBoundingClientRect().height > 0;
  }

  function measure() {
    var h = 0;
    document.querySelectorAll(SELECTORS).forEach(function (el) {
      if (onScreen(el)) h = Math.max(h, el.getBoundingClientRect().height);
    });
    // a bar taller than this is not a bar — don't push the launcher off screen
    if (h > 140) h = 0;
    document.documentElement.style.setProperty('--ne-bottom-bar-h', Math.round(h) + 'px');
  }

  // scroll fires far more often than the answer changes, so coalesce to a frame
  var queued = false;
  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () { queued = false; measure(); });
  }

  measure();
  window.addEventListener('resize', schedule);
  window.addEventListener('orientationchange', schedule);
  // the PDP's buy bar is display:none until the page is scrolled past the fold
  window.addEventListener('scroll', schedule, {passive: true});
  document.addEventListener('DOMContentLoaded', measure);
})();
